/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListGatewaySessionsRequest,
  ListGatewaySessionsSecurity,
  Sort,
  Status,
} from "../models/operations/listgatewaysessions.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildGatewaySessionsInfiniteQuery,
  buildGatewaySessionsQuery,
  GatewaySessionsInfiniteQueryData,
  GatewaySessionsPageParams,
  GatewaySessionsQueryData,
  prefetchGatewaySessions,
  prefetchGatewaySessionsInfinite,
  queryKeyGatewaySessions,
  queryKeyGatewaySessionsInfinite,
} from "./gatewaySessions.core.js";
export {
  buildGatewaySessionsInfiniteQuery,
  buildGatewaySessionsQuery,
  type GatewaySessionsInfiniteQueryData,
  type GatewaySessionsPageParams,
  type GatewaySessionsQueryData,
  prefetchGatewaySessions,
  prefetchGatewaySessionsInfinite,
  queryKeyGatewaySessions,
  queryKeyGatewaySessionsInfinite,
};

export type GatewaySessionsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listGatewaySessions gatewaySessions
 */
export function useGatewaySessions(
  request?: ListGatewaySessionsRequest | undefined,
  security?: ListGatewaySessionsSecurity | undefined,
  options?: QueryHookOptions<
    GatewaySessionsQueryData,
    GatewaySessionsQueryError
  >,
): UseQueryResult<GatewaySessionsQueryData, GatewaySessionsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildGatewaySessionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listGatewaySessions gatewaySessions
 */
export function useGatewaySessionsSuspense(
  request?: ListGatewaySessionsRequest | undefined,
  security?: ListGatewaySessionsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    GatewaySessionsQueryData,
    GatewaySessionsQueryError
  >,
): UseSuspenseQueryResult<GatewaySessionsQueryData, GatewaySessionsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildGatewaySessionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listGatewaySessions gatewaySessions
 */
export function useGatewaySessionsInfinite(
  request?: ListGatewaySessionsRequest | undefined,
  security?: ListGatewaySessionsSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    GatewaySessionsInfiniteQueryData,
    GatewaySessionsQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<GatewaySessionsInfiniteQueryData, GatewaySessionsPageParams>,
  GatewaySessionsQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    GatewaySessionsInfiniteQueryData,
    GatewaySessionsQueryError,
    InfiniteData<GatewaySessionsInfiniteQueryData, GatewaySessionsPageParams>,
    QueryKey,
    GatewaySessionsPageParams
  >({
    ...buildGatewaySessionsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listGatewaySessions gatewaySessions
 */
export function useGatewaySessionsInfiniteSuspense(
  request?: ListGatewaySessionsRequest | undefined,
  security?: ListGatewaySessionsSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    GatewaySessionsInfiniteQueryData,
    GatewaySessionsQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<GatewaySessionsInfiniteQueryData, GatewaySessionsPageParams>,
  GatewaySessionsQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    GatewaySessionsInfiniteQueryData,
    GatewaySessionsQueryError,
    InfiniteData<GatewaySessionsInfiniteQueryData, GatewaySessionsPageParams>,
    QueryKey,
    GatewaySessionsPageParams
  >({
    ...buildGatewaySessionsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setGatewaySessionsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gatewayId?: string | undefined;
      search?: string | undefined;
      serverId?: string | undefined;
      providerId?: string | undefined;
      status?: Status | undefined;
      createdAfter?: Date | undefined;
      createdBefore?: Date | undefined;
      sort?: Sort | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    },
  ],
  data: GatewaySessionsQueryData,
): GatewaySessionsQueryData | undefined {
  const key = queryKeyGatewaySessions(...queryKeyBase);

  return client.setQueryData<GatewaySessionsQueryData>(key, data);
}

export function invalidateGatewaySessions(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gatewayId?: string | undefined;
      search?: string | undefined;
      serverId?: string | undefined;
      providerId?: string | undefined;
      status?: Status | undefined;
      createdAfter?: Date | undefined;
      createdBefore?: Date | undefined;
      sort?: Sort | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "gatewaySessions", "list", ...queryKeyBase],
  });
}

export function invalidateAllGatewaySessions(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "gatewaySessions", "list"],
  });
}
